'use client'

import * as React from 'react'
import { RadioGroup as RadixRadioGroup } from 'radix-ui'
import { Icon } from '../icon'
import { faCircle } from '@fortawesome/pro-solid-svg-icons'
import { mtc } from '../../../utils/style-helpers'
import {
  iconStyles,
  indicatorStyles,
  itemStyles,
  rootStyles,
  itemContainerStyles,
  itemLabelStyles,
} from './styles'

export interface RadioGroupProps extends React.ComponentProps<
  typeof RadixRadioGroup.Root
> {
  legend?: string
}

export const RadioGroup = ({
  className,
  legend,
  disabled,
  ...props
}: RadioGroupProps) => {
  return (
    <fieldset className={mtc(rootStyles(), className)}>
      {legend && <legend className="label-sm mb-4">{legend}</legend>}
      <RadixRadioGroup.Root
        className="grid gap-2"
        {...props}
        disabled={disabled}
      />
    </fieldset>
  )
}

export const RadioGroupItem = ({
  className,
  label,
  id,
  ...props
}: React.ComponentProps<typeof RadixRadioGroup.Item> & { label?: string }) => {
  const isDisabled = props.disabled || false
  return (
    <label className={itemContainerStyles()} htmlFor={id}>
      <RadixRadioGroup.Item
        id={id}
        className={mtc(itemStyles(), className)}
        {...props}
      >
        <RadixRadioGroup.Indicator className={indicatorStyles()}>
          <Icon
            icon={faCircle}
            className={iconStyles()}
            color="neutral-700"
            variant="checkbox"
            size="xs"
          />
        </RadixRadioGroup.Indicator>
      </RadixRadioGroup.Item>
      <span className={mtc(itemLabelStyles({ disabled: isDisabled }))}>
        {label}
      </span>
    </label>
  )
}

RadioGroup.displayName = RadixRadioGroup.Root.displayName
RadioGroupItem.displayName = RadixRadioGroup.Item.displayName

export const BooleanRadioGroup = ({
  value,
  onValueChange,
  yesLabel = 'Yes',
  noLabel = 'No',
  ...props
}: Omit<RadioGroupProps, 'value' | 'onValueChange'> & {
  value?: boolean
  onValueChange?: (value: boolean) => void
  yesLabel?: string
  noLabel?: string
}) => {
  return (
    <RadioGroup
      {...props}
      value={value?.toString()}
      onValueChange={(val) => onValueChange?.(val === 'true')}
    >
      <RadioGroupItem value="true" label={yesLabel} />
      <RadioGroupItem value="false" label={noLabel} />
    </RadioGroup>
  )
}
